// frontend/src/components/conversion/useConversionTrigger.js

import { useEffect, useRef } from "react";

/** Visible-tab time after which the popup appears with no scroll at all (owner D1). */
export const DWELL_MS = 25000;
/** Share of the scrollable height that opens the earlier, scroll path (owner D1). */
export const SCROLL_RATIO = 0.35;
/**
 * The scroll path's floor of visible-tab time. It keeps the "never on first
 * paint" rule and stops a quick first flick down the page from popping the card.
 */
export const SCROLL_FLOOR_MS = 10000;
/**
 * Below this many scrollable pixels the scroll path does not count and the
 * trigger is time only - 35% of a page that barely scrolls is noise.
 */
export const MIN_SCROLLABLE_PX = 160;

/**
 * The scroll facts the trigger needs, read from the live document.
 *
 * @returns {{scrollable: number, top: number}}
 */
function readScroll() {
  const root = document.scrollingElement || document.documentElement;
  const viewport = window.innerHeight || root.clientHeight || 0;
  const scrollable = Math.max(0, (root.scrollHeight || 0) - viewport);
  const top = window.scrollY || window.pageYOffset || root.scrollTop || 0;
  return { scrollable, top };
}

/**
 * Fires `onFire` once, at the FIRST moment either path holds:
 *   - time:   `dwellMs` of VISIBLE-tab time on this page, scroll or not; or
 *   - scroll: the visitor has scrolled to `scrollRatio` of the scrollable
 *             height AND `scrollFloorMs` of visible-tab time has passed. Reached
 *             before the floor, it fires when the floor is reached; reached
 *             after, it fires at the moment of scrolling.
 * i.e. (visible >= dwellMs) OR (scroll >= scrollRatio AND visible >= scrollFloorMs).
 *
 * The visible-time clock pauses while `document.hidden` and resumes where it
 * stopped. Both paths need visible time, which is what guarantees the popup
 * never appears on first paint. A page with less than MIN_SCROLLABLE_PX of
 * scroll is time only. One passive scroll listener, throttled to one read per
 * frame; every listener and timer is removed the moment it fires, when
 * `enabled` turns false, when `resetKey` changes (an SPA navigation is a new
 * page, so the count starts over) and on unmount.
 *
 * @param {object}   options
 * @param {boolean}  options.enabled  - arm the trigger
 * @param {string}   options.resetKey - restarts the count when it changes (pathname)
 * @param {Function} options.onFire   - called once per armed run
 * @param {number}   [options.dwellMs=DWELL_MS]
 * @param {number}   [options.scrollRatio=SCROLL_RATIO]
 * @param {number}   [options.scrollFloorMs=SCROLL_FLOOR_MS]
 */
export default function useConversionTrigger({
  enabled,
  resetKey,
  onFire,
  dwellMs = DWELL_MS,
  scrollRatio = SCROLL_RATIO,
  scrollFloorMs = SCROLL_FLOOR_MS,
}) {
  // The latest callback, so a parent re-render does not restart the clock.
  const onFireRef = useRef(onFire);
  onFireRef.current = onFire;

  useEffect(() => {
    if (!enabled) return undefined;

    // Visible time banked from earlier visible stretches; `startedAt` marks
    // the start of the current stretch (null while hidden or finished).
    let elapsed = 0;
    let startedAt = null;
    let timer = null;
    let rafId = null;
    // Separate from rafId: a frame callback may run before its id is stored.
    let framePending = false;
    let scrollDone = false;
    let finished = false;

    const visibleMs = () => elapsed + (startedAt === null ? 0 : Date.now() - startedAt);

    // The visible-time milestone that would fire next: the scroll floor once
    // the scroll line is crossed, otherwise the plain dwell.
    const nextTargetMs = () => (scrollDone ? Math.min(scrollFloorMs, dwellMs) : dwellMs);

    const clearTimer = () => {
      if (timer === null) return;
      clearTimeout(timer);
      timer = null;
    };

    const stopClock = () => {
      clearTimer();
      if (startedAt === null) return;
      elapsed += Date.now() - startedAt;
      startedAt = null;
    };

    const teardown = () => {
      finished = true;
      stopClock();
      if (rafId !== null && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(rafId);
      }
      rafId = null;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", onScroll);
    };

    /** Fires when either path holds now; otherwise (re)arms the next milestone. */
    const evaluate = () => {
      if (finished) return;
      if (visibleMs() >= nextTargetMs()) {
        teardown();
        onFireRef.current?.();
        return;
      }
      clearTimer();
      if (startedAt === null) return; // hidden: the clock is paused
      timer = setTimeout(() => {
        timer = null;
        evaluate();
      }, Math.max(0, nextTargetMs() - visibleMs()));
    };

    const startClock = () => {
      if (finished || startedAt !== null || document.hidden) return;
      startedAt = Date.now();
      evaluate();
    };

    function onVisibility() {
      if (document.hidden) stopClock();
      else startClock();
    }

    const measureScroll = () => {
      framePending = false;
      rafId = null;
      if (finished || scrollDone) return;
      const { scrollable, top } = readScroll();
      if (scrollable >= MIN_SCROLLABLE_PX && top / scrollable >= scrollRatio) {
        scrollDone = true;
        window.removeEventListener("scroll", onScroll);
        evaluate();
      }
    };

    function onScroll() {
      if (framePending) return;
      framePending = true;
      if (typeof window.requestAnimationFrame === "function") {
        const id = window.requestAnimationFrame(measureScroll);
        // A frame that already ran (synchronously) leaves nothing to cancel.
        if (framePending) rafId = id;
      } else {
        measureScroll();
      }
    }

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("scroll", onScroll, { passive: true });
    // A restored scroll position already past the line counts.
    measureScroll();
    startClock();

    return teardown;
  }, [enabled, resetKey, dwellMs, scrollRatio, scrollFloorMs]);
}
